import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { FaCrown, FaQuestionCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthProvider";
import {
  GET_VIP_MEMBERS,
  CLEAR_TIPPER_NOT_ON_DISCORD,
  END_VIP_GRANT,
  MARK_TIPPER_NOT_ON_DISCORD,
  REVOKE_VIP,
  UNLINK_TIPPER_DISCORD,
  UNLINK_USER_DISCORD,
} from "../../../queries/mutation/broadcaster/vipQueries";
import LoadingPage from "../../LoadingPage";
import FanclubSyncModal from "./FanclubSyncModal";
import GrantVipModal, { GrantTarget } from "./GrantVipModal";
import VipPurchaseModal from "./VipPurchaseModal";
import LinkDiscordModal from "./LinkDiscordModal";

interface VipMember {
  id: string;
  // Either may be missing: a Reinstorm VIP with no CB, or a CB tipper with
  // no Reinstorm account.
  tipperId: string | null;
  userId: string | null;
  username: string;
  reinUsername: string | null;
  isFanclubVip: boolean;
  grantUntil: string | null;
  lastSeen: string | null;
  firstJoinedFanclub: string | null;
  isStale: boolean;
  isLinked: boolean;
  linkSource: "reinstorm" | "manual" | null;
  discordName: string | null;
  discordLinkedBy: string | null;
  notOnDiscord: boolean;
  notOnDiscordBy: string | null;
}

type View = "stale" | "needsDiscord" | "notOnDiscord" | "granted" | "all";

type PurchaseItem = {
  type: string;
  name: string | null;
  isActive: boolean;
} | null;

type LastSync = { at: string; by: string | null; count: number | null } | null;

const DAY_MS = 24 * 60 * 60 * 1000;

const describeLastSeen = (lastSeen: string | null) => {
  if (!lastSeen) return "Never";
  const days = Math.floor((Date.now() - new Date(lastSeen).getTime()) / DAY_MS);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : "—";

const toGrantTarget = (m: VipMember): GrantTarget => ({
  userId: m.userId,
  tipperId: m.tipperId,
  label: m.username,
  grantUntil: m.grantUntil,
});

// What each row can do; shared by the phone cards and the desktop table so
// the two layouts can't drift apart.
type RowHandlers = {
  isBroadcaster: boolean;
  onLink: (m: VipMember) => void;
  onUnlink: (m: VipMember) => void;
  onMarkNotOnDiscord: (m: VipMember) => void;
  onClearNotOnDiscord: (m: VipMember) => void;
  onGrant: (m: VipMember) => void;
  onEndGrant: (m: VipMember) => void;
  onRevoke: (m: VipMember) => void;
};

const NameAndBadges = ({ member }: { member: VipMember }) => (
  <>
    <div className="font-semibold break-words">
      {member.username}
      {member.isStale && (
        <span className="badge badge-warning badge-sm ml-2 align-middle">
          stale
        </span>
      )}
    </div>
    {member.reinUsername && member.reinUsername !== member.username && (
      <div className="text-xs text-base-content/60 break-words">
        Reinstorm: {member.reinUsername}
      </div>
    )}
    <div className="flex flex-wrap gap-1 mt-1">
      {member.isFanclubVip && (
        <span className="badge badge-success badge-outline badge-xs">
          fan club
        </span>
      )}
      {member.grantUntil && (
        <span className="badge badge-accent badge-outline badge-xs whitespace-nowrap">
          VIP until {formatDate(member.grantUntil)}
        </span>
      )}
    </div>
  </>
);

const DiscordStatus = ({
  member,
  handlers,
}: {
  member: VipMember;
  handlers: RowHandlers;
}) => {
  if (!member.tipperId && !member.linkSource) {
    // No CB tipper to link by hand - they link Discord to their own
    // Reinstorm account.
    return (
      <span className="text-xs text-base-content/60">No Discord linked yet</span>
    );
  }
  if (member.linkSource === "reinstorm") {
    return (
      <div className="flex flex-wrap items-center gap-1">
        <span
          className="badge badge-success badge-sm max-w-full truncate"
          title="Linked through their own Reinstorm account"
        >
          {member.discordName ? `@${member.discordName}` : "Reinstorm account"}
        </span>
        {/* Only a Reinstorm user can be unlinked this way. */}
        {member.userId && (
          <button
            className="btn btn-ghost btn-xs"
            onClick={() => handlers.onUnlink(member)}
          >
            Unlink
          </button>
        )}
      </div>
    );
  }
  if (member.linkSource === "manual") {
    return (
      <div className="flex flex-wrap items-center gap-1">
        <span
          className="badge badge-info badge-sm max-w-full truncate"
          title={
            member.discordLinkedBy
              ? `Linked by ${member.discordLinkedBy}`
              : "Linked by hand"
          }
        >
          @{member.discordName ?? "linked"}
        </span>
        <button
          className="btn btn-ghost btn-xs"
          onClick={() => handlers.onUnlink(member)}
        >
          Unlink
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {member.notOnDiscord && (
        <span
          className="badge badge-warning badge-outline badge-sm whitespace-nowrap"
          title={
            member.notOnDiscordBy ? `Marked by ${member.notOnDiscordBy}` : undefined
          }
        >
          not on Discord
        </span>
      )}
      <button
        className="btn btn-outline btn-xs"
        onClick={() => handlers.onLink(member)}
      >
        Link Discord
      </button>
      {member.notOnDiscord ? (
        <button
          className="btn btn-ghost btn-xs"
          onClick={() => handlers.onClearNotOnDiscord(member)}
        >
          Undo
        </button>
      ) : (
        <button
          className="btn btn-ghost btn-xs"
          title="They're not on Discord - move them to the Not on Discord list"
          onClick={() => handlers.onMarkNotOnDiscord(member)}
        >
          Mark not on Discord
        </button>
      )}
    </div>
  );
};

const RowActions = ({
  member,
  handlers,
}: {
  member: VipMember;
  handlers: RowHandlers;
}) => (
  <>
    {handlers.isBroadcaster && (
      <button
        className="btn btn-accent btn-outline btn-xs whitespace-nowrap"
        onClick={() => handlers.onGrant(member)}
      >
        {member.grantUntil ? "Add days" : "Grant"}
      </button>
    )}
    {member.grantUntil && (
      <button
        className="btn btn-warning btn-outline btn-xs whitespace-nowrap"
        onClick={() => handlers.onEndGrant(member)}
      >
        End grant
      </button>
    )}
    {/* Only fan club VIP is revoked this way; a grant is ended. */}
    {member.isFanclubVip && member.tipperId && (
      <button
        className="btn btn-error btn-xs"
        onClick={() => handlers.onRevoke(member)}
      >
        Revoke
      </button>
    )}
  </>
);

const VipMembers = () => {
  const [view, setView] = useState<View>("stale");
  const [showHelp, setShowHelp] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<VipMember | null>(null);
  const [linking, setLinking] = useState<VipMember | null>(null);
  const [pendingUnlink, setPendingUnlink] = useState<VipMember | null>(null);
  const [syncing, setSyncing] = useState(false);
  // "new" opens the grant dialog with a search instead of a preselected row.
  const [granting, setGranting] = useState<GrantTarget | "new" | null>(null);
  const [settingPurchase, setSettingPurchase] = useState(false);
  const [pendingEnd, setPendingEnd] = useState<VipMember | null>(null);
  const { user } = useAuth();
  // Granting VIP is broadcaster-only; mods can still end a grant.
  const isBroadcaster = !!user?.isBroadcaster;

  const { loading, error, data, refetch } = useQuery(GET_VIP_MEMBERS, {
    fetchPolicy: "network-only",
  });

  const [revokeVip, { loading: revoking }] = useMutation(REVOKE_VIP, {
    onCompleted: () => {
      toast.success(`Revoked VIP for ${pendingRevoke?.username}`);
      setPendingRevoke(null);
      refetch();
    },
    onError: (e) => {
      toast.error(`Failed to revoke VIP: ${e.message}`);
    },
  });

  const [unlinkDiscord, { loading: unlinking }] = useMutation(
    UNLINK_TIPPER_DISCORD,
    {
      onCompleted: () => {
        toast.success(`Unlinked Discord from ${pendingUnlink?.username}`);
        setPendingUnlink(null);
        refetch();
      },
      onError: (e) => {
        toast.error(`Failed to unlink: ${e.message}`);
      },
    },
  );

  const [unlinkUserDiscord, { loading: unlinkingUser }] = useMutation(
    UNLINK_USER_DISCORD,
    {
      onCompleted: () => {
        toast.success(`Unlinked Discord from ${pendingUnlink?.username}`);
        setPendingUnlink(null);
        refetch();
      },
      onError: (e) => {
        toast.error(`Failed to unlink: ${e.message}`);
      },
    },
  );

  const [endGrant, { loading: ending }] = useMutation(END_VIP_GRANT, {
    onCompleted: () => {
      toast.success(`Ended granted VIP for ${pendingEnd?.username}`);
      setPendingEnd(null);
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  // Both are one click to undo, so no confirm step.
  const [markNotOnDiscord] = useMutation(MARK_TIPPER_NOT_ON_DISCORD, {
    onCompleted: () => refetch(),
    onError: (e) => toast.error(e.message),
  });
  const [clearNotOnDiscord] = useMutation(CLEAR_TIPPER_NOT_ON_DISCORD, {
    onCompleted: () => refetch(),
    onError: (e) => toast.error(e.message),
  });

  const members: VipMember[] = data?.getVipMembers?.members ?? [];
  const staleDays: number = data?.getVipMembers?.staleDays ?? 30;
  const lastSync: LastSync = data?.getVipMembers?.lastSync ?? null;
  const purchaseItem: PurchaseItem = data?.getVipMembers?.purchaseItem ?? null;
  const views: { value: View; label: string; members: VipMember[] }[] = [
    { value: "stale", label: "Stale", members: members.filter((m) => m.isStale) },
    {
      value: "needsDiscord",
      label: "Needs Discord",
      members: members.filter((m) => !m.isLinked && !m.notOnDiscord),
    },
    {
      value: "notOnDiscord",
      label: "Not on Discord",
      members: members.filter((m) => m.notOnDiscord),
    },
    {
      value: "granted",
      label: "Granted",
      members: members.filter((m) => m.grantUntil),
    },
    { value: "all", label: "All VIPs", members },
  ];
  const current = views.find((v) => v.value === view) ?? views[0];
  const visibleMembers = current.members;
  const emptyText: Record<View, string> = {
    stale: "No stale VIP members",
    needsDiscord: "Every VIP has Discord linked or is marked as not on Discord",
    notOnDiscord: "Nobody is marked as not on Discord",
    granted: "Nobody has granted VIP right now",
    all: "No VIP members yet",
  };

  const handlers: RowHandlers = {
    isBroadcaster,
    onLink: setLinking,
    onUnlink: setPendingUnlink,
    onMarkNotOnDiscord: (m) =>
      markNotOnDiscord({ variables: { tipperId: m.tipperId } }),
    onClearNotOnDiscord: (m) =>
      clearNotOnDiscord({ variables: { tipperId: m.tipperId } }),
    onGrant: (m) => setGranting(toGrantTarget(m)),
    onEndGrant: setPendingEnd,
    onRevoke: setPendingRevoke,
  };

  const purchaseText = purchaseItem
    ? purchaseItem.name
      ? `Buying "${purchaseItem.name}" on the menu adds a month of VIP.${
          purchaseItem.isActive
            ? ""
            : " It's switched off right now, so nobody can buy it."
        }`
      : "The menu item set to sell VIP isn't on the menu anymore."
    : 'No menu item sells VIP yet - use "VIP menu item" to pick one.';
  const syncText = lastSync
    ? `Last synced ${new Date(lastSync.at).toLocaleString()}${
        lastSync.by ? ` by ${lastSync.by}` : ""
      }${lastSync.count != null ? ` (${lastSync.count} members)` : ""}.`
    : "The fan club list hasn't been synced yet.";

  return (
    <div className="bg-base-200 min-h-full flex flex-col p-0 sm:p-4 gap-4">
      <div className="card bg-base-100 flex-1 flex flex-col">
        <div className="card-body flex flex-col flex-1 !p-3 sm:!p-4 gap-3">
          <div className="flex items-center gap-2">
            <FaCrown size={24} className="text-yellow-500 shrink-0" />
            <h2 className="card-title text-xl sm:text-2xl m-0">VIP Members</h2>
            <button
              className="btn btn-ghost btn-sm btn-circle"
              onClick={() => setShowHelp(true)}
              aria-label="How VIP works"
              title="How VIP works"
            >
              <FaQuestionCircle size={20} className="text-base-content/60" />
            </button>
            <button
              className="btn btn-primary btn-sm ml-auto"
              onClick={() => refetch()}
            >
              Refresh
            </button>
          </div>

          {/* Two per row on a phone, one row from sm up. */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
            {isBroadcaster && (
              <>
                <button
                  className="btn btn-accent btn-sm"
                  onClick={() => setGranting("new")}
                >
                  Grant VIP
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => setSettingPurchase(true)}
                >
                  VIP menu item
                </button>
              </>
            )}
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setSyncing(true)}
            >
              Sync fan club list
            </button>
          </div>

          {data?.getVipMembers?.rolesPaused && (
            <div className="alert alert-warning py-2 text-sm">
              Automatic Discord VIP roles are paused in Settings - VIP is still
              tracked here, but nobody's role is being added or removed.
            </div>
          )}

          {/* One scrollable row on a phone instead of a stack of tabs. */}
          <div
            role="tablist"
            className="tabs tabs-boxed flex flex-nowrap overflow-x-auto w-full sm:w-fit"
          >
            {views.map((v) => (
              <button
                key={v.value}
                role="tab"
                aria-selected={view === v.value}
                className={`tab whitespace-nowrap shrink-0 ${
                  view === v.value ? "tab-active" : ""
                }`}
                onClick={() => setView(v.value)}
              >
                {v.label} ({v.members.length})
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <LoadingPage />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-error text-xl font-bold mb-2">
                Error loading VIP members
              </div>
              <div className="text-gray-400">{error.message}</div>
            </div>
          ) : visibleMembers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              {emptyText[view]}
            </div>
          ) : (
            <>
              {/* Phones: one card per member, so nothing has to squeeze. */}
              <ul className="sm:hidden flex flex-col gap-2">
                {visibleMembers.map((member) => (
                  <li
                    key={member.id}
                    className="rounded-lg border border-base-300 bg-base-200/50 p-3 flex flex-col gap-2"
                  >
                    <NameAndBadges member={member} />
                    <div className="text-xs text-base-content/60">
                      {/* CB activity doesn't apply to a Reinstorm-only VIP. */}
                      {member.tipperId && (
                        <span>Last seen {describeLastSeen(member.lastSeen)}</span>
                      )}
                      {member.tipperId && member.firstJoinedFanclub && " · "}
                      {member.firstJoinedFanclub && (
                        <span>Joined {formatDate(member.firstJoinedFanclub)}</span>
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-base-content/50 mb-1">
                        Discord
                      </div>
                      <DiscordStatus member={member} handlers={handlers} />
                    </div>
                    <div className="flex flex-wrap gap-1 pt-1 border-t border-base-300">
                      <RowActions member={member} handlers={handlers} />
                    </div>
                  </li>
                ))}
              </ul>

              {/* Wider screens keep the table. */}
              <div className="hidden sm:block overflow-auto flex-1 min-h-0">
                <table className="table table-compact w-full">
                  <thead>
                    <tr className="border-b border-gray-600">
                      <th className="text-left">Username</th>
                      <th className="text-left">Last seen</th>
                      <th className="text-left">First joined</th>
                      <th className="text-left">Discord</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMembers.map((member) => (
                      <tr key={member.id} className="border-b border-gray-700">
                        <td>
                          <NameAndBadges member={member} />
                        </td>
                        <td className="whitespace-nowrap">
                          {member.tipperId
                            ? describeLastSeen(member.lastSeen)
                            : "—"}
                        </td>
                        <td className="whitespace-nowrap">
                          {formatDate(member.firstJoinedFanclub)}
                        </td>
                        <td>
                          <DiscordStatus member={member} handlers={handlers} />
                        </td>
                        <td className="text-right">
                          <div className="flex flex-wrap gap-1 justify-end">
                            <RowActions member={member} handlers={handlers} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {showHelp && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">How VIP works</h3>
            <div className="py-4 text-sm flex flex-col gap-3">
              <p>
                VIP follows Chaturbate fan club status automatically whenever a
                member chats, tips or joins. Chaturbate never says when someone
                leaves, so members with no activity for {staleDays}+ days are
                listed as stale for you to review - or sync the full fan club
                list from Chaturbate to settle everyone at once.
              </p>
              <p>The broadcaster can also grant VIP for a set number of days.</p>
              <div>
                <div className="font-semibold">Selling VIP</div>
                <p className="text-base-content/70">{purchaseText}</p>
              </div>
              <div>
                <div className="font-semibold">Fan club list</div>
                <p className="text-base-content/70">{syncText}</p>
              </div>
            </div>
            <div className="modal-action">
              <button className="btn" onClick={() => setShowHelp(false)}>
                Close
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setShowHelp(false)} />
        </div>
      )}

      {syncing && (
        <FanclubSyncModal
          onClose={() => setSyncing(false)}
          onApplied={() => {
            setSyncing(false);
            refetch();
          }}
        />
      )}

      {settingPurchase && (
        <VipPurchaseModal
          onClose={() => setSettingPurchase(false)}
          onSaved={() => {
            setSettingPurchase(false);
            refetch();
          }}
        />
      )}

      {granting && (
        <GrantVipModal
          target={granting === "new" ? undefined : granting}
          onClose={() => setGranting(null)}
          onGranted={() => {
            setGranting(null);
            refetch();
          }}
        />
      )}

      {pendingEnd && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              End granted VIP for {pendingEnd.username}?
            </h3>
            <p className="py-4 text-sm">
              {pendingEnd.isFanclubVip
                ? "They stay VIP through the fan club - this only ends the granted time."
                : "They stop being VIP now, and lose the Discord VIP role."}
            </p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setPendingEnd(null)}
                disabled={ending}
              >
                Cancel
              </button>
              <button
                className="btn btn-warning"
                disabled={ending}
                onClick={() =>
                  endGrant({
                    variables: {
                      userId: pendingEnd.userId,
                      tipperId: pendingEnd.userId ? null : pendingEnd.tipperId,
                    },
                  })
                }
              >
                {ending ? "Ending..." : "End grant"}
              </button>
            </div>
          </div>
        </div>
      )}

      {linking && (
        <LinkDiscordModal
          tipper={{ id: linking.tipperId ?? "", username: linking.username }}
          onClose={() => setLinking(null)}
          onLinked={() => {
            setLinking(null);
            refetch();
          }}
        />
      )}

      {pendingUnlink && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              Unlink {pendingUnlink.discordName ? `@${pendingUnlink.discordName}` : "Discord"}{" "}
              from {pendingUnlink.username}?
            </h3>
            <p className="py-4 text-sm">
              {pendingUnlink.linkSource === "reinstorm"
                ? "They linked this account themselves on Reinstorm. It loses the VIP role now, and they can link a different Discord on Link Accounts whenever they're ready. Their crimcoins stay on their Reinstorm account."
                : "That Discord account loses the VIP role. You can link a different one afterwards."}
            </p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setPendingUnlink(null)}
                disabled={unlinking || unlinkingUser}
              >
                Cancel
              </button>
              <button
                className="btn btn-warning"
                disabled={unlinking || unlinkingUser}
                onClick={() =>
                  pendingUnlink.linkSource === "reinstorm"
                    ? unlinkUserDiscord({
                        variables: { userId: pendingUnlink.userId },
                      })
                    : unlinkDiscord({
                        variables: { tipperId: pendingUnlink.tipperId },
                      })
                }
              >
                {unlinking || unlinkingUser ? "Unlinking..." : "Unlink"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingRevoke && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              Revoke fan club VIP for {pendingRevoke.username}?
            </h3>
            <p className="py-4 text-sm">
              {pendingRevoke.grantUntil
                ? `They stay VIP until ${formatDate(pendingRevoke.grantUntil)} from their granted time. `
                : pendingRevoke.isLinked
                  ? "Their Discord VIP role will be removed. "
                  : "They have no linked Discord account, so there's no role to remove. "}
              If they're actually still in the fan club, fan club VIP comes back
              automatically the next time they chat or tip.
            </p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setPendingRevoke(null)}
                disabled={revoking}
              >
                Cancel
              </button>
              <button
                className="btn btn-error"
                disabled={revoking}
                onClick={() =>
                  revokeVip({
                    variables: { tipperId: pendingRevoke.tipperId },
                  })
                }
              >
                {revoking ? "Revoking..." : "Revoke VIP"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VipMembers;
